/**
 * Степпер «Ход работ» заказа — общий для командной и клиентской карточки заказа.
 *
 * Единственное представление порядка и состояния этапов (centrika-docs!82; на клиентской
 * карточке — по замечанию заказчика: текстовый список этапов под степпером дублировал его).
 * Состояние этапа обозначено кружком — зелёный с галочкой: завершён; синий с номером: текущий;
 * серый с номером: предстоящий — и тултипом на кружке («Завершён»/«Текущий»/«Предстоит»),
 * открываемым и по наведению, и по фокусу с клавиатуры (триггер — кнопка). Под названием этапа:
 * у завершённого — дата завершения, у текущего и предстоящего с ориентиром — «до ДД.ММ.ГГГГ».
 */
import { Check } from 'lucide-react'
import type { StageDTO } from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/design-system/components/primitives/tooltip'

/** Состояние этапа для оформления кружка степпера и тултипа. */
type StageState = 'done' | 'current' | 'upcoming'

/** Текст тултипа кружка по состоянию этапа (centrika-docs!82). */
const STAGE_STATE_TOOLTIP: Record<StageState, string> = {
  done: 'Завершён',
  current: 'Текущий',
  upcoming: 'Предстоит',
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Подпись под названием этапа: дата завершения у завершённого (у этапов, завершённых до того,
 * как дата стала сохраняться, её нет — только галочка), ориентир «до …» у остальных.
 */
function stageCaption(stage: StageDTO, state: StageState): string | null {
  if (state === 'done') return stage.completedAt ? formatDate(stage.completedAt) : null
  return stage.targetDate ? `до ${formatDate(stage.targetDate)}` : null
}

export function OrderStageStepper({ stages }: { stages: StageDTO[] }) {
  return (
    <ol className="flex">
      {stages.map((s, i) => {
        const state: StageState = s.isDone ? 'done' : s.isCurrent ? 'current' : 'upcoming'
        const isLast = i === stages.length - 1
        const caption = stageCaption(s, state)
        return (
          <li
            key={s.key}
            aria-current={state === 'current' ? 'step' : undefined}
            className={cn(
              'relative flex flex-1 flex-col items-center gap-1.5 text-center text-12',
              state === 'current' ? 'font-medium text-accent-txt' : 'text-txt-tertiary',
            )}
          >
            <div className="relative flex h-step w-full items-center justify-center">
              {!isLast ? (
                <span
                  aria-hidden
                  className={cn(
                    'absolute left-1/2 top-1/2 h-0.5 w-full -translate-y-1/2',
                    state === 'done' ? 'bg-green' : 'bg-line',
                  )}
                />
              ) : null}
              <Tooltip>
                <TooltipTrigger
                  aria-label={`${s.label}: ${STAGE_STATE_TOOLTIP[state]}`}
                  className={cn(
                    'relative z-10 flex size-step items-center justify-center rounded-round text-12 font-semibold outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    state === 'done' && 'bg-green text-white',
                    state === 'current' && 'bg-accent text-white',
                    state === 'upcoming' && 'bg-gray-bg text-txt-secondary',
                  )}
                >
                  {state === 'done' ? (
                    <Check className="size-3.5 stroke-current" aria-hidden />
                  ) : (
                    i + 1
                  )}
                </TooltipTrigger>
                <TooltipContent>{STAGE_STATE_TOOLTIP[state]}</TooltipContent>
              </Tooltip>
            </div>
            <span>{s.label}</span>
            {caption ? <span className="text-11 text-txt-tertiary">{caption}</span> : null}
          </li>
        )
      })}
    </ol>
  )
}
